import React, { useState } from 'react';
import { Container, Row, Col, Form, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMapMarkerAlt, 
  faPhone, 
  faEnvelope, 
  faClock,
  faCheckCircle,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import './Services.css'; // Reutilizamos los estilos de Services
import './Contact.css'; // Estilos propios (hero con foto, issue #418)

const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    service: ''
  });

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Send email using EmailJS or similar service
      const response = await fetch('https://formspree.io/f/xwpevkbr', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          service: formData.service,
          message: formData.message,
          _replyto: formData.email,
          _subject: `Nuevo mensaje de contacto de ${formData.name}`,
        }),
      });

      if (response.ok) {
        setFormSubmitted(true);
        // Clear the form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: '',
          service: ''
        });
      } else {
        throw new Error('Error al enviar el formulario');
      }
    } catch (error) {
      console.error('Error:', error);
      alert(t('contactPage.formSection.error.message', 'Hubo un error al enviar el mensaje. Por favor intenta de nuevo.'));
    } finally {
      setSubmitting(false);
    }
  };

  // Get contact info and services from translations
  const contactInfoItems = [
    {
      icon: faMapMarkerAlt,
      titleKey: 'contactPage.infoSection.address.title',
      detailsKeys: t('contactPage.infoSection.address.details', { returnObjects: true }) || []
    },
    {
      icon: faPhone,
      titleKey: 'contactPage.infoSection.phone.title',
      detailsKeys: t('contactPage.infoSection.phone.details', { returnObjects: true }) || []
    },
    {
      icon: faEnvelope,
      titleKey: 'contactPage.infoSection.email.title',
      detailsKeys: t('contactPage.infoSection.email.details', { returnObjects: true }) || []
    },
    {
      icon: faClock,
      titleKey: 'contactPage.infoSection.hours.title',
      detailsKeys: t('contactPage.infoSection.hours.details', { returnObjects: true }) || []
    }
  ];

  const services = t('contactPage.formSection.serviceOptions', { returnObjects: true }) || {};
  const serviceKeys = Object.keys(services);

  return (
    <div className="services-page">
      {/* Hero Section Modern */}
      <section
        className="page-hero modern-page-hero contact-hero-photo"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/contacto/comercial.jpg)` }}
      >
        <div className="contact-hero-photo-overlay"></div>
        <Container>
          <div className="page-hero-content contact-hero-photo-content">
            <div className="hero-badge animate-appear">
              <span className="hero-badge-text">{t('contactPage.hero.badge')}</span>
            </div>
            <h1 className="animate-appear">{t('contactPage.hero.title')}</h1>
            <p className="animate-appear">
              {t('contactPage.hero.subtitle')}
            </p>
          </div>
        </Container>
      </section>

      {/* Contact Info and Form Section */}
      <section className="section modern-section">
        <Container>
          <h2 className="section-title modern-title">{t('contactPage.mainSection.title')}</h2>
          <p className="section-description text-center mb-5">
            {t('contactPage.mainSection.subtitle')}
          </p>

          <Row>
            <Col lg={4} md={5} className="mb-4 mb-md-0">
              <div className="contact-info-wrapper">
                {contactInfoItems.map((item, index) => (
                  <div className="contact-info-item modern-card mb-4" key={index}>
                    <div className="contact-icon modern-icon">
                      <FontAwesomeIcon icon={item.icon} />
                    </div>
                    <div className="contact-details">
                      <h3>{t(item.titleKey)}</h3>
                      {item.detailsKeys.map((detail, idx) => (
                        <p key={idx}>{detail}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="d-none d-lg-block mt-4">
                <img src={process.env.PUBLIC_URL + '/images/services/Network.png'} alt="Contacto" style={{width:'100%',borderRadius:'10px',boxShadow:'0 4px 16px rgba(0,0,0,0.08)'}} />
              </div>
            </Col>

            <Col lg={8} md={7}>
              <div className="contact-form-wrapper modern-card p-4">
                {formSubmitted ? (
                  <div className="text-center p-5">
                    <div className="success-icon mb-4">
                      <FontAwesomeIcon icon={faCheckCircle} size="4x" className="text-success" />
                    </div>
                    <h3>{t('contactPage.formSection.success.title')}</h3>
                    <p className="mb-4">
                      {t('contactPage.formSection.success.message')}
                    </p>
                    <Button 
                      variant="outline-primary" 
                      onClick={() => setFormSubmitted(false)}
                      className="modern-outline-button"
                    >
                      {t('contactPage.formSection.success.newMessageButton')}
                    </Button>
                  </div>
                ) : (
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col md={6} className="mb-3">
                        <Form.Group controlId="name">
                          <Form.Label>{t('contactPage.formSection.labels.name')}</Form.Label>
                          <Form.Control
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder={t('contactPage.formSection.placeholders.name')}
                            required
                            className="modern-input"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6} className="mb-3">
                        <Form.Group controlId="email">
                          <Form.Label>{t('contactPage.formSection.labels.email')}</Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder={t('contactPage.formSection.placeholders.email')}
                            required
                            className="modern-input"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6} className="mb-3">
                        <Form.Group controlId="phone">
                          <Form.Label>{t('contactPage.formSection.labels.phone')}</Form.Label>
                          <Form.Control
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder={t('contactPage.formSection.placeholders.phone')}
                            className="modern-input"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6} className="mb-3">
                        <Form.Group controlId="company">
                          <Form.Label>{t('contactPage.formSection.labels.company')}</Form.Label>
                          <Form.Control
                            type="text"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            placeholder={t('contactPage.formSection.placeholders.company')}
                            className="modern-input"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group controlId="service" className="mb-3">
                      <Form.Label>{t('contactPage.formSection.labels.service')}</Form.Label>
                      <Form.Select 
                        name="service" 
                        value={formData.service} 
                        onChange={handleChange}
                        className="modern-input"
                      >
                        <option value="">{t('contactPage.formSection.placeholders.service')}</option>
                        {serviceKeys.map((key) => (
                          <option key={key} value={key}>{services[key]}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group controlId="message" className="mb-4">
                      <Form.Label>{t('contactPage.formSection.labels.message')}</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={5}
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder={t('contactPage.formSection.placeholders.message')}
                        required
                        className="modern-input"
                      />
                    </Form.Group>

                    <div className="d-grid">
                      <Button variant="primary" type="submit" disabled={submitting} className="modern-button">
                        <FontAwesomeIcon icon={faArrowRight} className="me-2" /> 
                        {submitting ? t('contactPage.formSection.submittingButton') : t('contactPage.formSection.submitButton')}
                      </Button>
                    </div>
                  </Form>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Map Section */}
      <section className="section bg-light modern-section-light">
        <Container>
          <h2 className="section-title modern-title">{t('contactPage.mapSection.title')}</h2>
          <div className="map-responsive-wrapper modern-card">
            {/* Issue #438: la URL anterior con pb=!1m18...(CID de un listado de Google
                Places) devolvía 404 desde Google Maps y el iframe pintaba en blanco.
                Se reemplaza por el formato q=<lat>,<lng> (soportado sin API key, no
                depende de un CID de Places que puede desaparecer), con las mismas
                coordenadas del embed original. */}
            <iframe
              src="https://www.google.com/maps?q=9.927059792903089,-84.18602688520677&z=17&output=embed"
              width="100%"
              height="450" 
              style={{ border:0 }} 
              allowFullScreen="" 
              loading="lazy"
              title="Ubicación de ProMedic Group"
            ></iframe>
          </div>
          <div className="text-center mt-4">
            <a 
              href="https://www.google.com/maps/place/ProMedic+Group/@9.9270598,-84.1860269,17z/data=!3m1!4b1!4m5!3m4!1s0x8fa0fab85349e51b:0x963e8217013ef1c3!8m2!3d9.9270598!4d-84.1838382" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn btn-outline-secondary modern-outline-button"
            >
              {t('contactPage.mapSection.viewMapLink')} <FontAwesomeIcon icon={faArrowRight} className="ms-1" />
            </a>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default Contact; 